import React from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { DoubleSide, Shape, TextureLoader } from 'three';
import paperTexture from '../assets/rolodex-paper-texture.png';

useLoader.preload(TextureLoader, paperTexture);

function FolderPanels({ phase, direction, bottomY = -3.675, paperHeight = 6.48, hideTab = false }) {
  const texture = useLoader(TextureLoader, paperTexture);
  const frontPivot = React.useRef();
  const folderBody = React.useRef();
  const shape = React.useMemo(() => {
    const folder = new Shape();
    folder.moveTo(-5, bottomY);
    folder.lineTo(5, bottomY);
    folder.lineTo(5, 3.2);
    if (hideTab) {
      folder.lineTo(-5, 3.2);
    } else {
      folder.lineTo(-0.7, 3.2);
      folder.lineTo(-1.15, 3.675);
      folder.lineTo(-3.35, 3.675);
      folder.lineTo(-3.8, 3.2);
    }
    folder.lineTo(-5, 3.2);
    folder.closePath();
    return folder;
  }, [bottomY, hideTab]);
  const targetX = phase === 'closing' || phase === 'side' ? 0 : Math.PI / 2;
  const targetY = phase === 'side' ? direction * Math.PI / 2 : 0;
  const paperY = paperHeight === 6.48 ? -0.14 : (3.2 + bottomY) / 2 - 0.14;

  // Set the initial pose before paint; later phase changes must animate normally.
  React.useLayoutEffect(() => {
    frontPivot.current.rotation.x = targetX;
    folderBody.current.rotation.y = targetY;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    texture.anisotropy = 4;
    texture.needsUpdate = true;
  }, [texture]);

  useFrame((_state, delta) => {
    const amount = 1 - Math.exp(-delta * 10);
    frontPivot.current.rotation.x += (targetX - frontPivot.current.rotation.x) * amount;
    folderBody.current.rotation.y += (targetY - folderBody.current.rotation.y) * amount;
  });

  const material = { map: texture, color: '#d9c8a9', side: DoubleSide, roughness: 0.92, metalness: 0 };
  const geometrySettings = { depth: 0.16, bevelEnabled: true, bevelSegments: 2, bevelSize: 0.06, bevelThickness: 0.04 };

  return <group ref={folderBody}>
    <mesh position={[0, 0, -0.08]} receiveShadow>
      <extrudeGeometry args={[shape, geometrySettings]} />
      <meshStandardMaterial {...material} />
    </mesh>
    <mesh position={[0, paperY, 0.14]}>
      <planeGeometry args={[9.2, paperHeight]} />
      <meshBasicMaterial map={texture} color="#fffaf0" side={DoubleSide} transparent opacity={0.98} />
    </mesh>
    <mesh position={[0, paperY, 0.16]}>
      <planeGeometry args={[9.2, paperHeight]} />
      <meshBasicMaterial color="#fff7df" side={DoubleSide} transparent opacity={0.68} depthWrite={false} />
    </mesh>
    <group ref={frontPivot} position={[0, bottomY, 0.18]}>
      <mesh position={[0, -bottomY, 0]} castShadow>
        <extrudeGeometry args={[shape, geometrySettings]} />
        <meshStandardMaterial {...material} />
      </mesh>
    </group>
  </group>;
}

function FolderSceneContents({ phase, direction }) {
  const { camera, size } = useThree();
  const isMobile = size.width <= 600;
  const aspect = size.width / Math.max(size.height, 1);
  const viewWidth = 10;
  const viewHeight = isMobile ? viewWidth / Math.max(aspect, 0.1) : 8;
  const viewTop = isMobile ? 4.2 : 4;
  const viewBottom = viewTop - viewHeight;
  const bottomY = isMobile ? viewBottom + 0.5 : -3.675;
  const paperHeight = isMobile ? 3.2 - bottomY - 0.4 : 6.48;

  React.useLayoutEffect(() => {
    camera.left = -viewWidth / 2;
    camera.right = viewWidth / 2;
    camera.top = isMobile ? viewTop : viewHeight / 2;
    camera.bottom = isMobile ? viewBottom : -viewHeight / 2;
    camera.zoom = isMobile ? 1.03 : 1.05;
    camera.updateProjectionMatrix();
  }, [camera, isMobile, viewBottom, viewHeight, viewTop, viewWidth]);

  return <>
    <ambientLight intensity={1.7} />
    <directionalLight position={[-3, 5, 6]} intensity={1.4} />
    <FolderPanels phase={phase} direction={direction} bottomY={bottomY} paperHeight={paperHeight} hideTab={isMobile} />
  </>;
}

export default function Folder3DScene({ phase = 'open', direction = 1 }) {
  return <div className={`folder-3d-scene${phase !== 'open' ? ' is-transitioning' : ''}`}>
    <div className="folder-3d-stage">
      <Canvas orthographic dpr={[1, 1.5]} camera={{ position: [0, 0, 10], left: -5, right: 5, top: 4, bottom: -4, zoom: 1.05 }} gl={{ alpha: true, antialias: true, powerPreference: 'low-power' }}>
        <FolderSceneContents phase={phase} direction={direction} />
      </Canvas>
    </div>
  </div>;
}
